import type { PublicEvent } from "../types/public";
import { formatDoorCurtainSlots, publicPerformersList, weekdayJp } from "../types/public";

/** 告知管理の「コピペに含める項目」と同じキー構成 */
export type PublicSharePickKey = "title" | "date" | "reception" | "url" | "time" | "detail";

export const DEFAULT_PUBLIC_SHARE_PICK: Record<PublicSharePickKey, boolean> = {
  title: true,
  date: true,
  reception: true,
  url: true,
  time: true,
  detail: false,
};

export const PUBLIC_SHARE_FIELD_ORDER: { key: PublicSharePickKey; label: string }[] = [
  { key: "title", label: "タイトル" },
  { key: "date", label: "開催日" },
  { key: "reception", label: "受付期間" },
  { key: "url", label: "公式URL" },
  { key: "time", label: "公演時間" },
  { key: "detail", label: "詳細" },
];

export function buildPublicCopyLines(e: PublicEvent, picks: Record<string, boolean>): string {
  const lines: string[] = [];
  if (picks.title) lines.push(String(e.event_name ?? "").trim());
  if (picks.date) lines.push(`開催日: ${e.event_date}`);
  if (picks.reception) {
    const pt = String(e.reception_period_text ?? "").trim();
    const rs = String(e.reception_start ?? "").trim();
    const re = String(e.reception_end ?? "").trim();
    if (pt) lines.push(`受付: ${pt}`);
    else if (rs || re) lines.push(`受付期間: ${rs}〜${re}`);
  }
  if (picks.url) {
    const u = String(e.detail_url ?? "").trim();
    if (u) lines.push(`公式URL: ${u}`);
  }
  if (picks.time) {
    const t = [String(e.curtain_time ?? "").trim() || String(e.time_start ?? "").trim(), String(e.time_end ?? "").trim()].filter(Boolean).join("〜");
    if (t) lines.push(`公演時間: ${t}`);
    const slots = formatDoorCurtainSlots(e);
    if (slots) lines.push(`開場・開演: ${slots}`);
  }
  if (picks.detail) {
    const desc = String(e.description ?? "").trim();
    const perf = String(e.performance_text ?? "").trim();
    if (desc) lines.push(`詳細: ${desc}`);
    else if (perf) lines.push(`本番・メモ: ${perf}`);
  }
  return lines.join("\n");
}

/** 管理画面の xIntentText と同じ体裁（表サイトの公開 JSON 用） */
export function xIntentPublicText(e: PublicEvent): string {
  const name = String(e.event_name ?? "").trim();
  const date = e.event_date;
  const url = String(e.detail_url ?? "").trim() || String(e.reserve_url ?? "").trim();
  return `${name}\n${date}${url ? `\n${url}` : ""}\n\n#佳林ちゃんに会いに行ってみよう`;
}

function formatJpLongDate(iso: string): string {
  const base = String(iso ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(base)) return String(iso ?? "").trim() || "—";
  const [y, m, d] = base.split("-").map(Number);
  const wd = weekdayJp(base);
  return `${y}年${m}月${d}日(${wd})`;
}

/** 表サイト：ワンタップコピー用（項目選択なし） */
export function buildPublicOneTapCopyText(e: PublicEvent): string {
  const performer = publicPerformersList(e).join("・") || "宮本佳林";
  const title = String(e.event_name ?? "").trim() || "（イベント名未設定）";
  const lines: string[] = ["━━━━━━━━━━", performer, title, ""];
  lines.push("日程：");
  lines.push(formatJpLongDate(e.event_date));
  lines.push("");
  const venueLine = [e.prefecture, e.venue].map((x) => String(x ?? "").trim()).filter(Boolean).join(" ");
  if (venueLine) {
    lines.push("会場：");
    lines.push(venueLine);
    lines.push("");
  }
  const open = String(e.curtain_time ?? "").trim() || String(e.time_start ?? "").trim();
  if (open) {
    lines.push("開演：");
    lines.push(open);
    lines.push("");
  }
  const du = String(e.detail_url ?? "").trim();
  if (du) {
    lines.push("詳細：");
    lines.push(du);
    lines.push("");
  }
  const ru = String(e.reserve_url ?? "").trim();
  if (ru) {
    lines.push("申込み：");
    lines.push(ru);
    lines.push("");
  }
  lines.push("━━━━━━━━━━");
  return lines.join("\n");
}

/** 表サイト：X 用に絵文字付きで整形（項目選択なし） */
export function buildPublicOneTapXText(e: PublicEvent): string {
  const performer = publicPerformersList(e).join("・") || "宮本佳林";
  const title = String(e.event_name ?? "").trim() || "（イベント名未設定）";
  const bits: string[] = ["━━━━━━━━━━", `${performer}`, `${title}🫶`, ""];
  bits.push(`🗓${formatJpLongDate(e.event_date)}`);
  const venueLine = [e.prefecture, e.venue].map((x) => String(x ?? "").trim()).filter(Boolean).join(" ");
  if (venueLine) bits.push(`📍${venueLine}`);
  const open = String(e.curtain_time ?? "").trim() || String(e.time_start ?? "").trim();
  if (open) bits.push(`⏰${open}開演`);
  const du = String(e.detail_url ?? "").trim();
  if (du) {
    bits.push("");
    bits.push("詳細はこちら🫶");
    bits.push(du);
  }
  const ru = String(e.reserve_url ?? "").trim();
  if (ru) {
    bits.push("");
    bits.push("申込みはこちら🫶");
    bits.push(ru);
  }
  bits.push("", "━━━━━━━━━━");
  return bits.join("\n");
}

/** X アプリを優先し、開けなければブラウザの投稿画面へ */
export function openXIntentCompose(text: string): void {
  const enc = encodeURIComponent(text);
  window.open(`twitter://post?message=${enc}`, "_blank");
  setTimeout(() => {
    window.open(`https://x.com/intent/tweet?text=${enc}`, "_blank");
  }, 380);
}
